// メインのUI。1ページ1モード（#board-frame の data-mode が 'ai' か 'twoPlayer'）。
// ルールは checkers.js、AI の呼び出しは ai.js（AI対戦ページだけ読み込まれる）。

const MSG = window.MESSAGES;
function fmt(template, params) {
    if (!params) return template;
    return String(template).replace(/\{(\w+)\}/g, (_, k) => params[k] ?? '');
}

// 初期表示に要らない処理を、描画が終わってから流すための共通ヘルパー
function whenIdle(task, timeout = 2000) {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(task, timeout ? { timeout } : undefined);
    else setTimeout(task, 0);
}

const loadedScripts = new Map();
function loadScriptOnce(src) {
    if (!loadedScripts.has(src)) {
        loadedScripts.set(src, new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => { loadedScripts.delete(src); reject(new Error(`Failed to load ${src}`)); };
            document.head.appendChild(script);
        }));
    }
    return loadedScripts.get(src);
}

// feedback.js は ☰ → フィードバックまで要らないので遅延読込する。ただし「動かなかったときのエラー」を
// 添えることに価値があるので、収集だけは最初から始めておき、feedback.js が読み込まれた時点で引き継ぐ
const FEEDBACK_SCRIPT_SRC = '/feedback.js';
const MAX_EARLY_ERRORS = 3;
window.__checkersEarlyErrors = [];

function stackHead(error) {
    const stack = typeof error?.stack === 'string' ? error.stack : '';
    if (!stack) return '';
    const frames = stack.split('\n').map(l => l.trim())
        .filter(l => l.startsWith('at ') || l.includes('@')).slice(0, 2)
        .map(l => l.replace(/https?:\/\/[^/]+/g, ''));
    return frames.length ? ` | ${frames.join(' < ')}` : '';
}
function recordEarlyError(source, message) {
    if (!message || !window.__checkersEarlyErrors) return;
    window.__checkersEarlyErrors.push({ source, message: String(message).slice(0, 300), at: Date.now() });
    if (window.__checkersEarlyErrors.length > MAX_EARLY_ERRORS) window.__checkersEarlyErrors.shift();
}
window.addEventListener('error', e => {
    const where = e.filename ? ` @${String(e.filename).split('/').pop()}:${e.lineno}:${e.colno || 0}` : '';
    recordEarlyError('error', (e.message || 'error') + where + stackHead(e.error));
});
window.addEventListener('unhandledrejection', e => {
    recordEarlyError('promise', (e.reason?.message || e.reason || 'unhandledrejection') + stackHead(e.reason));
});

// ---- 計測と広告（load 後に読み込む。先に置くと LCP と帯域を奪い合う）----
const GA_ID = 'G-29HQ99PYVJ'; // GA4 の測定ID。空のあいだは計測を読み込まない
const AD_CLIENT = 'ca-pub-1918692579240633';

function gtagEvent(name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
}

// ---- モードと定数 -------------------------------------------------------------
const C = window.Checkers;
const AI = window.CheckersAI || null;
const boardFrame = document.getElementById('board-frame');
const MODE = boardFrame ? boardFrame.dataset.mode : 'ai';
const IS_AI = MODE === 'ai';
const SETTINGS_KEY = 'checkersSettings';
const PROGRESS_KEY = 'checkersProgress';
const STATE_KEY = IS_AI ? 'checkersAIState' : 'checkers2PState';
const STATE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const HOP_MS = 170;           // 1跳びのアニメーション時間
const AI_DELAY_MS = 350;      // 人の手のあと、AIが考え始めるまでの間
// build.mjs の CROWN_SVG と同じ文字列（test/build-pages.test.js が検証）
const CROWN_SVG = '<svg class="crown" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 17h16l1.2-9-4.6 3.4L12 5l-4.6 6.4L2.8 8z" fill="currentColor"/><rect x="4" y="18.5" width="16" height="2" rx="1" fill="currentColor"/></svg>';

function opposite(color) { return color === 'black' ? 'red' : 'black'; }

// ---- 設定・進み具合 -----------------------------------------------------------
const settings = {
    level: AI ? AI.DEFAULT_LEVEL : 'medium',
    playerColor: 'black',   // 'black' | 'red' | 'random'
    forceCapture: true,
    showMoves: true,
    sound: true,
    flipAI: false,
    flip2P: false
};
let progress = AI ? AI.normalizeProgress(null) : { cleared: {} };

function loadSettings() {
    try {
        const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || 'null');
        if (saved && typeof saved === 'object') {
            // ai.js の無い2人対戦ページでも、保存済みのレベルはそのまま持ち回す（saveSettings が丸ごと書き戻すため）
            settings.level = AI ? AI.normalizeLevel(saved.level) : (typeof saved.level === 'string' ? saved.level : settings.level);
            if (['black', 'red', 'random'].includes(saved.playerColor)) settings.playerColor = saved.playerColor;
            if (typeof saved.forceCapture === 'boolean') settings.forceCapture = saved.forceCapture;
            if (typeof saved.showMoves === 'boolean') settings.showMoves = saved.showMoves;
            if (typeof saved.sound === 'boolean') settings.sound = saved.sound;
            if (typeof saved.flipAI === 'boolean') settings.flipAI = saved.flipAI;
            if (typeof saved.flip2P === 'boolean') settings.flip2P = saved.flip2P;
        }
        if (AI) progress = AI.normalizeProgress(JSON.parse(localStorage.getItem(PROGRESS_KEY) || 'null'));
    } catch (e) {
        console.error(MSG.errors.settingsLoadFailed, e);
    }
    // 取り強制なしのままでは Master 以上を選べない（Marcher は取り強制でしか指せない）
    if (AI && !settings.forceCapture && AI.usesMarcher(settings.level)) settings.level = AI.FALLBACK_LEVEL;
}

function saveSettings() {
    try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
        console.error(MSG.errors.settingsSaveFailed, e);
    }
}

function saveProgress() {
    try {
        localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
    } catch (e) {
        console.error(MSG.errors.settingsSaveFailed, e);
    }
}

// ---- 効果音 -------------------------------------------------------------------
// preload='none' で「読み込み開始と同時のダウンロード」を止め、load 後のアイドル時間に取得する
const sounds = {};
for (const name of ['move', 'capture', 'king', 'win', 'lose']) {
    const audio = new Audio();
    audio.preload = 'none';
    audio.src = `/sounds/${name}.mp3`;
    sounds[name] = audio;
}
const SOUND_VOLUME = { move: 0.55, capture: 0.6, king: 0.45, win: 0.4, lose: 0.35 };

function preloadSounds() {
    if (!settings.sound) return;
    for (const audio of Object.values(sounds)) {
        if (audio.preload === 'none') audio.preload = 'auto';
    }
}

function playSound(name) {
    if (!settings.sound) return;
    const sound = sounds[name].cloneNode(true);
    sound.volume = SOUND_VOLUME[name];
    sound.play().catch(err => console.log(MSG.errors.soundPlayFailed, err));
}

// ---- 対局の状態 ---------------------------------------------------------------
let game = new C.CheckersGame({ forceCapture: settings.forceCapture });
let playerColor = 'black';             // AI対戦であなたが持つ色（この対局で確定した値）
let gameMeta = newGameMeta();
let ai = null;
let aiThinking = false;
let aiRequestGen = 0;
let aiMoveTimer = null;
let aiStatusOverride = null;           // 'loading' | 'fallback' | null
let animating = false;
let selected = null;                   // 選択中のマス（0〜31）
let jumpPath = null;                   // 連続ジャンプを1跳びずつ入力しているときの経路
let hint = null;                       // ヒントの手
let toastTimer = null;
let gameEndTimer = null;

function newGameMeta() {
    return { level: settings.level, undoUsed: false, hintUsed: false, engine: null, forceCapture: settings.forceCapture };
}

function aiColor() { return opposite(playerColor); }
function isHumanTurn() { return !game.gameOver && (!IS_AI || game.currentPlayer === playerColor); }
function levelName(id) { return MSG.levels[id] || id; }

// ---- 保存と復元 ---------------------------------------------------------------
let pendingSave = null;
function saveState() {
    if (pendingSave) return;
    const run = () => { pendingSave = null; writeState(); };
    if (typeof requestIdleCallback === 'function') {
        const id = requestIdleCallback(run, { timeout: 1000 });
        pendingSave = () => cancelIdleCallback(id);
    } else {
        const id = setTimeout(run, 0);
        pendingSave = () => clearTimeout(id);
    }
}
function flushSave() {
    if (!pendingSave) return;
    pendingSave();
    pendingSave = null;
    writeState();
}
function writeState() {
    try {
        localStorage.setItem(STATE_KEY, JSON.stringify({
            v: 1, game: game.toJSON(), playerColor, meta: gameMeta, timestamp: Date.now()
        }));
    } catch (e) {
        console.error(MSG.errors.gameStateSaveFailed, e);
    }
}
function clearSavedState() {
    if (pendingSave) { pendingSave(); pendingSave = null; }
    try { localStorage.removeItem(STATE_KEY); } catch (e) { console.error(MSG.errors.gameStateSaveFailed, e); }
}
function loadState() {
    try {
        const saved = JSON.parse(localStorage.getItem(STATE_KEY) || 'null');
        if (!saved || saved.v !== 1 || !saved.timestamp || Date.now() - saved.timestamp > STATE_MAX_AGE_MS) return false;
        const restored = C.CheckersGame.fromJSON(saved.game);
        if (!restored || restored.moveCount === 0) return false;
        game = restored;
        if (IS_AI) {
            playerColor = saved.playerColor === 'red' ? 'red' : 'black';
            gameMeta = { ...newGameMeta(), ...(saved.meta && typeof saved.meta === 'object' ? saved.meta : {}) };
            gameMeta.forceCapture = gameMeta.forceCapture !== false && game.forceCapture;
            if (AI && (!AI.LEVEL_IDS.includes(gameMeta.level))) gameMeta.level = settings.level;
        }
        return true;
    } catch (e) {
        console.error(MSG.errors.gameStateLoadFailed, e);
        return false;
    }
}

// ---- 盤の描画 -----------------------------------------------------------------
const board = document.getElementById('board');
const boardWrap = board ? board.parentNode : null;
const squareEls = board ? Array.from(board.children) : [];
const sqEls = new Array(32);           // マス番号 → 要素（反転で入れ替わる）
const sqOfEl = new Map();              // 要素 → マス番号
let highlighted = [];                  // 今光っている要素（解除用）

function bottomColor() {
    const base = IS_AI ? playerColor : 'black';
    const flipped = IS_AI ? settings.flipAI : settings.flip2P;
    return flipped ? opposite(base) : base;
}

function mapSquares() {
    const flipped = bottomColor() !== 'black';
    sqOfEl.clear();
    for (let s = 0; s < 32; s++) {
        const row = C.SQ_ROW[s], col = C.SQ_COL[s];
        const idx = flipped ? (7 - row) * 8 + (7 - col) : row * 8 + col;
        sqEls[s] = squareEls[idx];
        sqOfEl.set(squareEls[idx], s);
    }
}

function pieceCode(piece) {
    return piece ? (piece.color === 'black' ? 'b' : 'r') + (piece.king ? 'K' : '') : '';
}

function pieceHtml(piece) {
    if (!piece) return '';
    return `<span class="pc ${piece.color === 'black' ? 'blk' : 'red'}${piece.king ? ' king' : ''}">${piece.king ? CROWN_SVG : ''}</span>`;
}

// 連続ジャンプの途中入力を反映した表示用の局面
function displayPieceAt(s) {
    if (!jumpPath) return game.pieceAt(s);
    const from = jumpPath[0];
    const cur = jumpPath[jumpPath.length - 1];
    if (s === from && from !== cur) return null;
    if (s === cur) return game.pieceAt(from);
    if (capturedAlong(jumpPath).has(s)) return null;
    return game.pieceAt(s);
}

function capturedAlong(path) {
    const set = new Set();
    for (let i = 1; i < path.length; i++) {
        const a = path[i - 1], b = path[i];
        if (Math.abs(C.SQ_ROW[a] - C.SQ_ROW[b]) === 2) {
            set.add(C.squareAt((C.SQ_ROW[a] + C.SQ_ROW[b]) / 2, (C.SQ_COL[a] + C.SQ_COL[b]) / 2));
        }
    }
    return set;
}

// 静的HTMLの駒をそのまま引き継ぎ、変わったマスだけ書き換える
function renderBoard() {
    if (!board) return;
    for (let s = 0; s < 32; s++) {
        const el = sqEls[s];
        const code = pieceCode(displayPieceAt(s));
        if (el.dataset.p === undefined) el.dataset.p = pieceCode(readPiece(el));
        if (el.dataset.p !== code) {
            el.dataset.p = code;
            el.innerHTML = pieceHtml(code ? { color: code[0] === 'b' ? 'black' : 'red', king: code.length > 1 } : null);
        } else if (el.firstElementChild && el.firstElementChild.style.visibility) {
            el.firstElementChild.style.visibility = ''; // アニメーション中に隠した駒を戻す
        }
    }
    applyHighlights();
}

function readPiece(el) {
    const pc = el.firstElementChild;
    if (!pc || !pc.classList.contains('pc')) return null;
    return { color: pc.classList.contains('blk') ? 'black' : 'red', king: pc.classList.contains('king') };
}

function clearHighlights() {
    hideWarn(); // 警告カードは点滅と一緒に出すので、盤の表示が変わったら一緒に消す（待った・新規対局・反転・設定変更など）
    for (const el of highlighted) {
        el.classList.remove('sel', 'tgt', 'mv', 'hsel', 'htgt', 'shake', 'capflash');
        const pc = el.querySelector(':scope > .pc');
        if (pc) pc.classList.remove('cap');
        const dot = el.querySelector(':scope > .dot');
        if (dot) dot.remove();
    }
    highlighted = [];
}

function mark(s, cls) {
    const el = sqEls[s];
    el.classList.add(cls);
    highlighted.push(el);
    return el;
}

function addDot(s, hintDot) {
    const el = sqEls[s];
    if (!el.querySelector(':scope > .dot')) {
        const dot = document.createElement('span');
        dot.className = hintDot ? 'dot dhint' : 'dot';
        el.appendChild(dot);
    }
    highlighted.push(el);
}

function candidateMoves() {
    if (!jumpPath) return [];
    return game.movesFrom(jumpPath[0]).filter(m => jumpPath.every((s, i) => m.path[i] === s) && m.path.length >= jumpPath.length);
}

function applyHighlights() {
    clearHighlights();
    if (!isHumanTurn()) return;

    if (jumpPath) {
        const cur = jumpPath[jumpPath.length - 1];
        mark(cur, 'sel');
        for (const m of candidateMoves()) {
            if (m.path.length <= jumpPath.length) continue;
            const next = m.path[jumpPath.length];
            if (settings.showMoves) {
                mark(next, 'tgt');
                addDot(next, false);
                const mid = C.squareAt((C.SQ_ROW[cur] + C.SQ_ROW[next]) / 2, (C.SQ_COL[cur] + C.SQ_COL[next]) / 2);
                const pc = mid >= 0 ? sqEls[mid].querySelector(':scope > .pc') : null;
                if (pc) { pc.classList.add('cap'); highlighted.push(sqEls[mid]); }
            }
        }
        return;
    }

    if (selected !== null) {
        mark(selected, 'sel');
        if (settings.showMoves) {
            for (const m of game.movesFrom(selected)) {
                const next = m.path[1];
                mark(next, 'tgt');
                addDot(next, false);
                if (m.capMask) {
                    const mid = C.squareAt((C.SQ_ROW[selected] + C.SQ_ROW[next]) / 2, (C.SQ_COL[selected] + C.SQ_COL[next]) / 2);
                    const pc = sqEls[mid].querySelector(':scope > .pc');
                    if (pc) { pc.classList.add('cap'); highlighted.push(sqEls[mid]); }
                }
            }
        }
    } else if (settings.showMoves) {
        for (const s of game.mustCaptureSquares()) mark(s, 'mv');
    }

    if (hint) {
        mark(hint.from, 'hsel');
        mark(hint.path[1], 'htgt');
        addDot(hint.path[1], true);
    }
}

// 昇格した駒を0.3秒光らせる（style.css の .promo）
function flashPromotion(s) {
    const pc = sqEls[s].querySelector(':scope > .pc');
    if (!pc) return;
    pc.classList.add('promo');
    setTimeout(() => pc.classList.remove('promo'), 700);
}

// ---- 移動アニメーション（跳びごとにゴースト駒を動かす）------------------------------
function reducedMotion() {
    return document.hidden || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

// path[fromIndex] から最後まで動かす。DOM は呼び出し側が最後に renderBoard で書き換える
function animatePath(path, fromIndex, piece, done) {
    if (!board || reducedMotion() || fromIndex >= path.length - 1) { done(); return; }
    animating = true;
    const size = board.clientWidth / 8;
    const pos = s => {
        const el = sqEls[s];
        const idx = squareEls.indexOf(el);
        return { x: (idx & 7) * size, y: (idx >> 3) * size };
    };
    const ghost = document.createElement('div');
    ghost.className = 'pcghost';
    ghost.style.width = ghost.style.height = `${size}px`;
    ghost.innerHTML = pieceHtml(piece);
    const start = pos(path[fromIndex]);
    ghost.style.transform = `translate(${start.x}px, ${start.y}px)`;
    boardWrap.appendChild(ghost);
    const origin = sqEls[path[fromIndex]].querySelector(':scope > .pc');
    if (origin) origin.style.visibility = 'hidden';

    let i = fromIndex;
    const step = () => {
        const from = path[i], to = path[i + 1];
        const target = pos(to);
        // 2回の rAF でトランジションを確実に始める
        requestAnimationFrame(() => requestAnimationFrame(() => {
            ghost.style.transform = `translate(${target.x}px, ${target.y}px)`;
        }));
        setTimeout(() => {
            if (Math.abs(C.SQ_ROW[from] - C.SQ_ROW[to]) === 2) {
                const mid = C.squareAt((C.SQ_ROW[from] + C.SQ_ROW[to]) / 2, (C.SQ_COL[from] + C.SQ_COL[to]) / 2);
                const victim = sqEls[mid].querySelector(':scope > .pc');
                if (victim) victim.style.visibility = 'hidden';
            }
            i++;
            if (i < path.length - 1) step();
            else {
                ghost.remove();
                animating = false;
                done();
            }
        }, HOP_MS + 30);
    };
    step();
}

// ---- 名札・状態表示 ------------------------------------------------------------
const announcer = document.getElementById('announcer');
let lastAnnouncement = '';
function announce(text) {
    if (!announcer || text === lastAnnouncement) return;
    lastAnnouncement = text;
    announcer.textContent = text;
}

function renderNameTags() {
    const bottom = bottomColor();
    paintTag('top', opposite(bottom));
    paintTag('bot', bottom);
    if (board) board.setAttribute('aria-busy', aiThinking ? 'true' : 'false');
    if (game.gameOver) {
        const r = game.gameResult;
        announce(r.winner ? fmt(MSG.gameEnd.colorWins, { color: MSG.colors[r.winner] }) : MSG.gameEnd.draw);
    } else if (IS_AI) {
        announce(game.currentPlayer === playerColor ? MSG.status.yourTurn : MSG.status.computerThinking);
    } else {
        announce(fmt(MSG.status.toMove, { color: MSG.colors[game.currentPlayer] }));
    }
}

function paintTag(pos, color) {
    const wrap = document.getElementById(`nt-${pos}`);
    if (!wrap) return;
    const isHuman = !IS_AI || color === playerColor;
    const result = game.gameResult;
    const isTurn = !game.gameOver && game.currentPlayer === color;
    const isWinner = Boolean(result && result.winner === color);

    document.getElementById(`nt-${pos}-pip`).className = `pip ${color === 'black' ? 'blk' : 'red'}`;
    document.getElementById(`nt-${pos}-name`).textContent = IS_AI ? (isHuman ? MSG.nameTag.you : MSG.nameTag.computer) : MSG.colors[color];
    const order = color === 'black' ? MSG.nameTag.movesFirst : MSG.nameTag.movesSecond;
    document.getElementById(`nt-${pos}-sub`).textContent = IS_AI
        ? (isHuman ? `${MSG.colors[color]} · ${order}` : `${levelName(gameMeta.level)} · ${MSG.colors[color]}`)
        : order;

    let status = '';
    let busy = false;
    if (isWinner) status = MSG.nameTag.wins;
    else if (isTurn) {
        if (IS_AI && !isHuman) {
            status = aiStatusOverride === 'loading' ? MSG.nameTag.loading : MSG.nameTag.thinking;
            busy = true;
        } else if (IS_AI) status = MSG.nameTag.yourTurn;
        else status = fmt(MSG.nameTag.toMove, { color: MSG.colors[color] });
    }
    const st = document.getElementById(`nt-${pos}-status`);
    st.textContent = status;
    st.classList.toggle('busy', busy);

    const chip = document.getElementById(`nt-${pos}-chip`);
    chip.hidden = !(isTurn && isHuman && game.forceCapture && game.mustCaptureSquares().length > 0);
    const inputCaptures = jumpPath && color !== game.currentPlayer ? capturedAlong(jumpPath).size : 0;
    document.getElementById(`nt-${pos}-count`).textContent = String(game.count(color) - inputCaptures);
    wrap.classList.toggle('on', isTurn || isWinner);
}

const boardToast = document.getElementById('board-toast');
// text は文字列か要素の配列。place（'top' / 'bottom'）を渡すと盤のその半分に警告カードとして出す
function toast(text, ms = 2200, place) {
    if (!boardToast) return;
    if (Array.isArray(text)) boardToast.replaceChildren(...text);
    else boardToast.textContent = text;
    boardToast.classList.toggle('warn', Boolean(place));
    boardToast.classList.toggle('lo', place === 'bottom');
    boardToast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => boardToast.classList.remove('show'), ms);
}

function hideWarn() {
    if (boardToast && boardToast.classList.contains('warn')) boardToast.classList.remove('show');
}

function updateButtons() {
    const undo = document.getElementById('undo-button');
    const redo = document.getElementById('redo-button');
    const hintButton = document.getElementById('hint-button');
    const minMoves = IS_AI && playerColor === 'red' ? 1 : 0;
    if (undo) undo.disabled = animating || game.moveCount <= minMoves;
    if (redo) redo.disabled = animating || !game.canRedo();
    if (hintButton) hintButton.disabled = !isHumanTurn() || animating;
}

// ---- 手の入力 -----------------------------------------------------------------
function clearSelection() {
    selected = null;
    jumpPath = null;
}

function clearHint() {
    if (!hint) return;
    hint = null;
    applyHighlights();
}

function onSquareClick(s) {
    if (animating) return;
    if (game.gameOver) { toast(MSG.status.gameOver); return; }
    if (IS_AI && game.currentPlayer !== playerColor) { toast(MSG.status.computerTurn); return; }
    hint = null;

    if (jumpPath) {
        const cur = jumpPath[jumpPath.length - 1];
        const cands = candidateMoves();
        if (s === cur) {
            const complete = cands.find(m => m.path.length === jumpPath.length);
            if (complete) commitMove(complete);
            return;
        }
        if (cands.some(m => m.path.length > jumpPath.length && m.path[jumpPath.length] === s)) {
            jumpPath.push(s);
            resolvePath();
        }
        return;
    }

    const piece = game.pieceAt(s);
    const own = piece && piece.color === game.currentPlayer;
    const ownMovable = own && game.movesFrom(s).length > 0;
    if (own && !ownMovable && game.mustCaptureSquares().length > 0) {
        selected = null;
        explainMustCapture(s);
        return;
    }
    if (selected === null) {
        if (ownMovable) { selected = s; applyHighlights(); }
        return;
    }
    if (s === selected) { selected = null; applyHighlights(); return; }
    if (game.movesFrom(selected).some(m => m.path[1] === s)) {
        jumpPath = [selected, s];
        selected = null;
        resolvePath();
        return;
    }
    selected = ownMovable ? s : null;
    applyHighlights();
}

// 取り強制で動かせない駒を触ったとき：その駒を揺らし、取るべき駒を点滅させ、理由をカードで伝える
function explainMustCapture(s) {
    const musts = game.mustCaptureSquares();
    applyHighlights();
    void sqEls[s].offsetWidth; // 同じ駒を続けて触っても揺れ直すように、外したクラスを一度反映させる
    mark(s, 'shake');
    for (const m of musts) mark(m, 'capflash');
    // カードは上なら表示上の1〜2行目、下なら5〜6行目を覆う。取るべき駒を隠す数が少ない側に出す（同数なら触った駒を隠さない側）
    const flipped = bottomColor() !== 'black';
    const hides = (sq, top) => { const r = flipped ? 7 - C.SQ_ROW[sq] : C.SQ_ROW[sq]; return r === top || r === top + 1; };
    const covered = top => musts.filter(m => hides(m, top)).length + (hides(s, top) ? 0.5 : 0);
    const t = MSG.status.mustCapture;
    // 設定が既にオフ（Master 以上の対局中に切り替えた等で、この対局だけ標準ルール）なら「オフにできます」は出さない
    const rows = [['b', t.title], ['span', t.rule]];
    if (settings.forceCapture) rows.push(['small', t.settings]);
    const lines = rows.map(([tag, text]) => {
        const el = document.createElement(tag);
        el.textContent = text;
        return el;
    });
    toast(lines, 6000, covered(1) <= covered(5) ? 'top' : 'bottom');
}

// 入力途中の経路から手を確定できるか判断する。続きが1通りなら自動で進める
function resolvePath() {
    const cands = candidateMoves();
    const complete = cands.find(m => m.path.length === jumpPath.length);
    const longer = cands.filter(m => m.path.length > jumpPath.length);
    if (complete && longer.length === 0) { commitMove(complete); return; }
    if (longer.length > 0 && !complete) {
        const nexts = new Set(longer.map(m => m.path[jumpPath.length]));
        if (nexts.size === 1) {
            // 続きが一本道なら、残りをまとめて確定してアニメーションで見せる
            const only = longer.length === 1 ? longer[0] : null;
            if (only) { commitMove(only); return; }
        }
    }
    renderBoard();
    renderNameTags();
    if (complete && longer.length > 0) toast(MSG.status.keepJumping);
}

// 手を確定する。alreadyShown は表示済みの跳びの数（人が1跳びずつ入力した分）
function commitMove(move) {
    const mover = game.currentPlayer;
    const piece = game.pieceAt(move.from);
    const shown = jumpPath ? jumpPath.length - 1 : 0;
    if (!game.makeMove(move)) { clearSelection(); renderBoard(); return; }
    clearSelection();
    hint = null;
    clearHighlights();
    playSound(move.capMask ? 'capture' : 'move');
    updateButtons();

    animatePath(move.path, shown, piece, () => {
        renderBoard();
        if (move.promote) { flashPromotion(move.to); playSound('king'); }
        renderNameTags();
        updateButtons();
        saveState();
        if (game.gameOver) onGameOver();
        else if (IS_AI && game.currentPlayer === aiColor()) scheduleAIMove();
    });
    if (IS_AI && mover === playerColor) aiStatusOverride = aiStatusOverride === 'fallback' ? 'fallback' : null;
}

// ---- AI ---------------------------------------------------------------------
function scheduleAIMove(delay = AI_DELAY_MS) {
    if (!ai || game.gameOver || game.currentPlayer !== aiColor()) return;
    clearTimeout(aiMoveTimer);
    aiThinking = true;
    renderNameTags();
    updateButtons();
    aiMoveTimer = setTimeout(() => { void makeAIMove(); }, delay);
}

function cancelAIMove() {
    clearTimeout(aiMoveTimer);
    if (ai) ai.cancel();
    aiThinking = false;
}

async function makeAIMove() {
    if (!ai || game.gameOver || game.currentPlayer !== aiColor()) { aiThinking = false; return; }
    const gen = ++aiRequestGen;
    const st = game.state;
    let move = null;
    try {
        const res = await ai.requestMove({ b: st.b, r: st.r, k: st.k, side: st.side }, game.forceCapture);
        if (gen !== aiRequestGen) return;
        move = game.legalMoves().find(m => m.capMask === res.move.capMask && m.path.length === res.move.path.length && m.path.every((s, i) => s === res.move.path[i]));
        if (!move) throw new Error('AI move is not legal here');
        if (res.engine === 'fallback' && aiStatusOverride !== 'fallback') {
            aiStatusOverride = 'fallback';
            toast(MSG.nameTag.fallback, 4000);
        }
        if (!gameMeta.engine) gameMeta.engine = res.engine;
    } catch (err) {
        if (err && err.cancelled) return;
        if (gen !== aiRequestGen) return;
        console.error(MSG.errors.aiFailed, err);
        // 最後の砦：合法手のどれかを指して対局を止めない
        const legal = game.legalMoves();
        move = legal[Math.floor(Math.random() * legal.length)] || null;
    }
    aiThinking = false;
    aiStatusOverride = aiStatusOverride === 'loading' ? null : aiStatusOverride;
    if (move) commitMove(move);
    else renderNameTags();
}

async function requestHint() {
    if (!ai || !isHumanTurn() || animating) return;
    clearSelection();
    const button = document.getElementById('hint-button');
    if (button) button.disabled = true;
    const st = game.state;
    const gen = aiRequestGen;
    try {
        const move = await ai.requestHint({ b: st.b, r: st.r, k: st.k, side: st.side }, game.forceCapture);
        if (gen !== aiRequestGen || !isHumanTurn() || !move) { toast(MSG.status.hintNone); return; }
        hint = move;
        gameMeta.hintUsed = true;
        applyHighlights();
        toast(MSG.status.hintReady);
        gtagEvent('hint', { level: gameMeta.level });
    } catch (err) {
        if (!(err && err.cancelled)) { console.error(MSG.errors.aiFailed, err); toast(MSG.status.hintNone); }
    } finally {
        updateButtons();
    }
}

// ---- 終局 -------------------------------------------------------------------
function onGameOver() {
    const result = game.gameResult;
    let outcome;
    if (!result.winner) outcome = 'draw';
    else if (IS_AI) outcome = result.winner === playerColor ? 'win' : 'lose';
    else outcome = result.winner;

    let unlocked = null;
    if (IS_AI && outcome === 'win' && AI && gameMeta.forceCapture && gameMeta.engine !== 'fallback') {
        unlocked = AI.recordWin(progress, gameMeta.level);
        saveProgress();
        renderDifficultyOptions();
        if (unlocked) gtagEvent('level_unlock', { level: unlocked });
    }
    if (outcome === 'win' || (!IS_AI && outcome !== 'draw')) playSound('win');
    else if (outcome === 'lose') playSound('lose');

    gtagEvent('game_end', {
        mode: IS_AI ? 'ai' : '2p',
        level: IS_AI ? gameMeta.level : undefined,
        engine: IS_AI ? (gameMeta.engine || 'local') : undefined,
        result: outcome,
        reason: result.reason,
        rule: game.forceCapture ? 'standard' : 'casual',
        undo: gameMeta.undoUsed ? 1 : 0,
        hint: gameMeta.hintUsed ? 1 : 0,
        moves: game.moveCount
    });
    if (ai) ai.resetLocal();
    clearTimeout(gameEndTimer);
    gameEndTimer = setTimeout(() => showGameEndModal(outcome, unlocked), 450);
}

let shareText = '';
function showGameEndModal(outcome, unlocked) {
    const modal = document.getElementById('game-end-modal');
    const result = game.gameResult;
    if (!modal || !result) return; // 表示前に新規対局が始まっていたら出さない
    const level = gameMeta.level;
    const variant = outcome === 'win' ? 'win' : outcome === 'lose' ? 'lose' : outcome === 'draw' ? 'draw' : 'win';
    // SVG 要素には hidden プロパティが無い（HTMLElement だけ）ので、属性で切り替える
    modal.querySelectorAll('#game-end-mark svg').forEach(svg => svg.toggleAttribute('hidden', svg.dataset.variant !== variant));

    const title = document.getElementById('game-end-title');
    const lead = document.getElementById('game-end-lead');
    const unlock = document.getElementById('game-end-unlock');
    const tip = document.getElementById('game-end-tip');
    const note = document.getElementById('game-end-note');
    const primary = document.getElementById('game-end-primary');
    const secondary = document.getElementById('game-end-secondary');
    const share = document.getElementById('game-end-share');
    unlock.hidden = true; tip.hidden = true; note.hidden = true; share.hidden = true; secondary.hidden = false;
    modal.dataset.primary = 'rematch';
    modal.dataset.secondary = 'close';

    const reasonText = result.reason === 'threefold' ? MSG.gameEnd.reasonThreefold
        : result.reason === 'fortyMove' ? MSG.gameEnd.reasonFortyMove
        : fmt(result.reason === 'noPieces' ? MSG.gameEnd.reasonNoPieces : MSG.gameEnd.reasonNoMoves, { loser: MSG.colors[opposite(result.winner || 'black')] });

    if (IS_AI && outcome === 'win') {
        title.textContent = MSG.gameEnd.youWin;
        lead.textContent = fmt(MSG.gameEnd.levelCleared, { level: levelName(level), pieces: game.count(playerColor) });
        const next = AI.nextLevel(level);
        if (unlocked) {
            unlock.hidden = false;
            document.getElementById('game-end-unlock-title').textContent = fmt(MSG.gameEnd.unlockedTitle, { level: levelName(unlocked) });
            const after = AI.nextLevel(unlocked);
            document.getElementById('game-end-unlock-sub').textContent = after
                ? fmt(MSG.gameEnd.unlockedSub, { level: levelName(unlocked), next: levelName(after) })
                : MSG.gameEnd.unlockedSubLast;
        } else if (!gameMeta.forceCapture && next && !AI.isUnlocked(progress, next)) {
            note.hidden = false;
            note.textContent = MSG.gameEnd.casualNote;
        }
        if (next && AI.isUnlocked(progress, next) && gameMeta.forceCapture) {
            primary.textContent = fmt(MSG.gameEnd.playNext, { level: levelName(next) });
            modal.dataset.primary = `level:${next}`;
            secondary.textContent = fmt(MSG.gameEnd.rematchOn, { level: levelName(level) });
            modal.dataset.secondary = 'rematch';
        } else {
            primary.textContent = MSG.gameEnd.rematch;
            secondary.textContent = MSG.gameEnd.close;
        }
        share.hidden = false;
        shareText = fmt(MSG.share.win, { level: levelName(level) });
    } else if (IS_AI && outcome === 'lose') {
        title.textContent = MSG.gameEnd.youLose;
        lead.textContent = fmt(MSG.gameEnd.loseLead, { level: levelName(level), yours: game.count(playerColor), theirs: game.count(aiColor()), moves: game.moveCount });
        tip.hidden = false;
        document.getElementById('game-end-tip-text').textContent = MSG.gameEnd.tips[Math.floor(Math.random() * MSG.gameEnd.tips.length)];
        primary.textContent = MSG.gameEnd.rematch;
        const lower = AI.previousLevel(level);
        if (lower) {
            secondary.textContent = fmt(MSG.gameEnd.tryLower, { level: levelName(lower) });
            modal.dataset.secondary = `level:${lower}`;
        } else secondary.textContent = MSG.gameEnd.close;
    } else if (outcome === 'draw') {
        title.textContent = MSG.gameEnd.draw;
        lead.textContent = IS_AI ? fmt(MSG.gameEnd.drawLead, { level: levelName(level), reason: reasonText }) : reasonText;
        if (IS_AI) {
            const next = AI.nextLevel(level);
            if (next && !AI.isUnlocked(progress, next)) { note.hidden = false; note.textContent = fmt(MSG.gameEnd.drawNote, { level: levelName(next) }); }
        }
        primary.textContent = MSG.gameEnd.rematch;
        secondary.textContent = MSG.gameEnd.close;
    } else {
        // 2人対戦の勝ち
        title.textContent = fmt(MSG.gameEnd.colorWins, { color: MSG.colors[result.winner] });
        lead.textContent = reasonText;
        primary.textContent = MSG.gameEnd.rematch;
        secondary.textContent = MSG.gameEnd.close;
        share.hidden = false;
        shareText = fmt(MSG.share.twoPlayer, { color: MSG.colors[result.winner] });
    }
    openModal(modal);
}

// ---- 対局の開始・やり直し ------------------------------------------------------
function resolvePlayerColor() {
    if (!IS_AI) return 'black';
    if (settings.playerColor === 'random') return Math.random() < 0.5 ? 'black' : 'red';
    return settings.playerColor;
}

function startNewGame() {
    cancelAIMove();
    clearTimeout(gameEndTimer);
    // Marcher は取り強制でしか指せない。取り強制オフのまま Master 以上が選ばれていたら（対局中に切り替えた場合など）ここで Expert に戻す
    if (AI && !settings.forceCapture && AI.usesMarcher(settings.level)) { settings.level = AI.FALLBACK_LEVEL; saveSettings(); }
    if (ai) ai.setLevel(settings.level);
    game = new C.CheckersGame({ forceCapture: settings.forceCapture });
    playerColor = resolvePlayerColor();
    gameMeta = newGameMeta();
    aiStatusOverride = null;
    clearSelection();
    hint = null;
    clearSavedState();
    mapSquares();
    renderBoard();
    renderNameTags();
    renderDifficultyOptions();
    refreshForceCaptureNote();
    updateButtons();
    if (IS_AI && game.currentPlayer === aiColor()) scheduleAIMove(500);
}

function undo() {
    if (animating || game.moveCount === 0) return;
    cancelAIMove();
    clearSelection();
    hint = null;
    if (IS_AI) {
        const minMoves = playerColor === 'red' ? 1 : 0;
        const plies = game.currentPlayer === playerColor ? 2 : 1;
        game.undo(Math.min(plies, game.moveCount - minMoves));
    } else {
        game.undo(1);
    }
    gameMeta.undoUsed = true;
    renderBoard();
    renderNameTags();
    updateButtons();
    saveState();
    if (IS_AI && !game.gameOver && game.currentPlayer === aiColor()) scheduleAIMove();
}

function redo() {
    if (animating || !game.canRedo()) return;
    cancelAIMove();
    clearSelection();
    hint = null;
    game.redo(IS_AI ? 2 : 1);
    renderBoard();
    renderNameTags();
    updateButtons();
    saveState();
    if (game.gameOver) return;
    if (IS_AI && game.currentPlayer === aiColor()) scheduleAIMove();
}

function flipBoard() {
    if (IS_AI) settings.flipAI = !settings.flipAI; else settings.flip2P = !settings.flip2P;
    saveSettings();
    clearHighlights();
    mapSquares();
    renderBoard();
    renderNameTags();
}

// ---- 難易度ドロップダウン ------------------------------------------------------
function renderDifficultyOptions() {
    const listbox = document.getElementById('difficulty-listbox');
    const value = document.getElementById('difficulty-trigger-value');
    if (!listbox || !AI) return;
    for (const option of listbox.querySelectorAll('[role="option"]')) {
        const id = option.dataset.level;
        const unlocked = AI.isUnlocked(progress, id);
        const casualBlocked = !settings.forceCapture && AI.usesMarcher(id);
        const cleared = AI.isCleared(progress, id);
        option.hidden = !AI.isVisible(progress, id);
        option.classList.toggle('done', cleared);
        const selected = id === settings.level;
        const blocked = !unlocked || (casualBlocked && !selected);
        option.classList.toggle('lk', blocked);
        option.classList.toggle('sel', selected);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (blocked) option.setAttribute('aria-disabled', 'true'); else option.removeAttribute('aria-disabled');
        option.querySelector('.chk').textContent = cleared ? '✓' : '';
        const sub = option.querySelector('.opt-sub');
        sub.textContent = !unlocked ? fmt(MSG.difficulty.lockedHint, { level: levelName(AI.previousLevel(id)) })
            : casualBlocked ? MSG.difficulty.standardOnly : '';
    }
    value.textContent = levelName(settings.level);
}

function selectLevel(id) {
    if (!AI || !AI.LEVEL_IDS.includes(id)) return false;
    if (!AI.isUnlocked(progress, id)) {
        toast(fmt(MSG.difficulty.lockedToast, { level: levelName(AI.previousLevel(id)), locked: levelName(id) }), 3000);
        return false;
    }
    if (id === settings.level) return true; // 選択中（Master 対局中に取り強制をオフにした場合も含む）はそのまま
    if (!settings.forceCapture && AI.usesMarcher(id)) {
        toast(fmt(MSG.difficulty.standardOnlyToast, { level: levelName(id) }), 3500);
        return false;
    }
    settings.level = id;
    saveSettings();
    if (ai) ai.setLevel(id);
    if (AI.usesMarcher(id) && ai) ai.preloadMarcher().catch(() => {});
    // 対局の途中でレベルを変えたら新しい対局にする（低いレベルで進めて最後だけ上げ、解放を取る抜け道を塞ぐ）
    if (game.moveCount > 0 && !game.gameOver) {
        startNewGame();
        toast(fmt(MSG.difficulty.newGameOn, { level: levelName(id) }));
        return true;
    }
    gameMeta.level = id;
    gameMeta.engine = null;
    renderDifficultyOptions();
    renderNameTags();
    saveState();
    return true;
}

function setupDifficultySelector() {
    const dropdown = document.getElementById('difficulty-dropdown');
    const trigger = document.getElementById('difficulty-trigger');
    const listbox = document.getElementById('difficulty-listbox');
    if (!dropdown || !trigger || !listbox) return;
    // 見えている項目だけ（解放状況で変わるので開くたびに取り直す）
    let options = [];
    let activeIndex = -1;

    const isOpen = () => !listbox.hidden;
    // scroll: 一覧が画面に収まらずスクロールするとき、選んだ項目を見える位置へ（開いたとき・キー操作。ホバーでは動かさない）
    const setActive = (index, scroll) => {
        activeIndex = index;
        options.forEach((opt, i) => opt.classList.toggle('active', i === index));
        if (index >= 0) {
            trigger.setAttribute('aria-activedescendant', options[index].id);
            if (scroll) options[index].scrollIntoView({ block: 'nearest' });
        } else trigger.removeAttribute('aria-activedescendant');
    };
    const open = () => {
        renderDifficultyOptions();
        options = Array.from(listbox.querySelectorAll('[role="option"]:not([hidden])'));
        listbox.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.classList.add('open');
        // 下に収まらなければ上に開き、どちらにも収まらなければ広いほうでスクロールさせる
        listbox.style.maxHeight = '';
        const gap = 14;
        const triggerRect = trigger.getBoundingClientRect();
        const below = window.innerHeight - triggerRect.bottom - gap;
        const above = triggerRect.top - gap;
        const height = listbox.scrollHeight;
        const up = height > below && above > below;
        dropdown.classList.toggle('up', up);
        const room = up ? above : below;
        if (height > room) listbox.style.maxHeight = `${Math.max(120, Math.floor(room))}px`;
        // 狭い画面で右にはみ出すぶんは左へずらす（横スクロールを起こさない）
        listbox.style.left = '';
        const viewportWidth = document.documentElement.clientWidth; // innerWidth はスクロールバー込みなので使わない
        listbox.style.maxWidth = `${viewportWidth - 24}px`;
        const overflow = listbox.getBoundingClientRect().right + 12 - viewportWidth;
        if (overflow > 0) listbox.style.left = `${-overflow}px`;
        setActive(Math.max(options.findIndex(o => o.dataset.level === settings.level), 0), true);
        if (ai && AI.LEVELS.some(l => l.engine === 'marcher' && AI.isUnlocked(progress, l.id))) {
            // 上位レベルを選べる人には、開いた時点でエンジンを先読みしておく
            whenIdle(() => ai.preloadMarcher().catch(() => {}), 300);
        }
    };
    const close = () => {
        listbox.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('open', 'up');
        listbox.style.maxHeight = '';
        listbox.style.left = '';
        listbox.style.maxWidth = '';
        setActive(-1);
    };
    const choose = index => {
        if (index < 0) return;
        if (selectLevel(options[index].dataset.level)) close();
    };

    trigger.addEventListener('click', () => (isOpen() ? close() : open()));
    trigger.addEventListener('keydown', e => {
        if (!isOpen()) {
            if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); open(); }
            return;
        }
        switch (e.key) {
            case 'ArrowDown': e.preventDefault(); setActive(Math.min(activeIndex + 1, options.length - 1), true); break;
            case 'ArrowUp': e.preventDefault(); setActive(Math.max(activeIndex - 1, 0), true); break;
            case 'Home': e.preventDefault(); setActive(0, true); break;
            case 'End': e.preventDefault(); setActive(options.length - 1, true); break;
            case 'Enter': case ' ': e.preventDefault(); choose(activeIndex); break;
            case 'Escape': e.preventDefault(); close(); break;
            case 'Tab': close(); break;
        }
    });
    listbox.addEventListener('mousedown', e => e.preventDefault());
    const indexOf = e => options.indexOf(e.target.closest('[role="option"]'));
    listbox.addEventListener('click', e => choose(indexOf(e)));
    listbox.addEventListener('mousemove', e => { const index = indexOf(e); if (index >= 0 && activeIndex !== index) setActive(index); });
    document.addEventListener('pointerdown', e => { if (isOpen() && !dropdown.contains(e.target)) close(); });
    renderDifficultyOptions();
}

// ---- モーダル -----------------------------------------------------------------
let openModalEl = null;
let modalOpener = null;
function openModal(modal) {
    if (openModalEl && openModalEl !== modal) closeModal(openModalEl);
    modalOpener = document.activeElement;
    modal.hidden = false;
    openModalEl = modal;
    const focusTarget = modal.querySelector('.btn.pri, textarea, button');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
}
function closeModal(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    if (openModalEl === modal) openModalEl = null;
    if (modalOpener && typeof modalOpener.focus === 'function') modalOpener.focus({ preventScroll: true });
    modalOpener = null;
}
function setupModalBasics(modal) {
    if (!modal) return;
    modal.addEventListener('click', e => { if (e.target === modal) closeModal(modal); });
    modal.addEventListener('keydown', e => {
        if (e.key !== 'Tab') return;
        const focusables = Array.from(modal.querySelectorAll('button, [href], select, textarea, input:not(.feedback-honeypot)'))
            .filter(el => !el.disabled && !el.hidden && el.offsetParent !== null);
        if (focusables.length === 0) return;
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
}
document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && openModalEl) { e.stopPropagation(); closeModal(openModalEl); }
}, true);

function setToggle(button, on) {
    button.classList.toggle('off', !on);
    button.setAttribute('aria-checked', on ? 'true' : 'false');
}

function setupSettings() {
    const modal = document.getElementById('settings-modal');
    const button = document.getElementById('settings-button');
    if (!modal || !button) return;
    setupModalBasics(modal);
    button.addEventListener('click', () => openModal(modal));
    document.getElementById('close-settings').addEventListener('click', () => closeModal(modal));
    document.getElementById('settings-done').addEventListener('click', () => closeModal(modal));

    const colorGroup = document.getElementById('setting-color');
    if (colorGroup) {
        const buttons = Array.from(colorGroup.querySelectorAll('.seg-btn'));
        const paint = () => buttons.forEach(b => { const on = b.dataset.color === settings.playerColor; b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
        paint();
        buttons.forEach(b => b.addEventListener('click', () => {
            if (b.dataset.color === settings.playerColor) return;
            settings.playerColor = b.dataset.color;
            saveSettings();
            paint();
            startNewGame(); // 色を変えたら新しい対局（途中の対局は色が入れ替わってしまうため）
        }));
    }

    const forceToggle = document.getElementById('setting-force-capture');
    setToggle(forceToggle, settings.forceCapture);
    forceToggle.addEventListener('click', () => {
        settings.forceCapture = !settings.forceCapture;
        setToggle(forceToggle, settings.forceCapture);
        saveSettings();
        const inProgress = game.moveCount > 0 && !game.gameOver;
        const marcherGame = IS_AI && AI && AI.usesMarcher(gameMeta.level);
        if (marcherGame && inProgress) {
            // Marcher は取り強制でしか指せないので、この対局は標準ルールのまま。次の対局（startNewGame）で Expert に戻す。
            // 盤が変わらない理由を設定行に出す（無反応に見えないように）
            refreshForceCaptureNote();
        } else {
            if (IS_AI && AI && !settings.forceCapture && AI.usesMarcher(settings.level)) {
                settings.level = AI.FALLBACK_LEVEL;
                saveSettings();
                if (ai) ai.setLevel(settings.level);
                if (!game.gameOver) gameMeta.level = settings.level; // 終わった対局の記録（名札・フィードバック）は書き換えない
            }
            const wasOver = game.gameOver;
            game.setForceCapture(settings.forceCapture);
            gameMeta.forceCapture = gameMeta.forceCapture && settings.forceCapture;
            clearSelection();
            hint = null;
            if (!wasOver) {
                // ルールが変わると「動ける手が無い」判定も変わりうる
                const result = game.deriveResult();
                if (result) game.history[game.historyIndex].result = result;
            }
            renderBoard();
            renderNameTags();
            updateButtons();
            saveState();
            if (!wasOver && game.gameOver) onGameOver();
            refreshForceCaptureNote();
        }
        renderDifficultyOptions();
    });
    refreshForceCaptureNote();

    const showToggle = document.getElementById('setting-show-moves');
    setToggle(showToggle, settings.showMoves);
    showToggle.addEventListener('click', () => {
        settings.showMoves = !settings.showMoves;
        setToggle(showToggle, settings.showMoves);
        saveSettings();
        applyHighlights();
    });

    const soundToggle = document.getElementById('setting-sound');
    setToggle(soundToggle, settings.sound);
    soundToggle.addEventListener('click', () => setSound(!settings.sound));
}

// 取り強制の設定行の副題。Master 以上の対局中に取り強制をオフにしたときは「この対局は標準ルールのまま」と説明する
function refreshForceCaptureNote() {
    const toggle = document.getElementById('setting-force-capture');
    const sub = toggle && toggle.parentNode.querySelector('.sub');
    if (!sub) return;
    if (sub.dataset.original === undefined) sub.dataset.original = sub.textContent;
    const marcherGame = IS_AI && AI && AI.usesMarcher(gameMeta.level) && game.moveCount > 0 && !game.gameOver;
    const explain = marcherGame && !settings.forceCapture && game.forceCapture;
    sub.textContent = explain ? fmt(MSG.difficulty.casualNextGame, { level: levelName(gameMeta.level) }) : sub.dataset.original;
    sub.classList.toggle('note', Boolean(explain));
}

function setSound(on) {
    settings.sound = on;
    saveSettings();
    const soundToggle = document.getElementById('setting-sound');
    if (soundToggle) setToggle(soundToggle, on);
    const mute = document.getElementById('mute-button');
    if (mute) {
        mute.setAttribute('aria-pressed', on ? 'false' : 'true');
        mute.setAttribute('aria-label', on ? mute.dataset.labelMute : mute.dataset.labelUnmute);
        mute.querySelector('.icon-sound-on').toggleAttribute('hidden', !on);
        mute.querySelector('.icon-sound-off').toggleAttribute('hidden', on);
    }
    if (on) preloadSounds();
}

function setupMenu() {
    const modal = document.getElementById('menu-modal');
    const button = document.getElementById('menu-button');
    if (!modal || !button) return;
    setupModalBasics(modal);
    button.addEventListener('click', () => openModal(modal));
    document.getElementById('close-menu').addEventListener('click', () => closeModal(modal));
    // ページ内リンクはメニューを閉じてから飛ぶ
    document.getElementById('menu-how-to-play').addEventListener('click', () => closeModal(modal));
    document.getElementById('menu-feedback').addEventListener('click', () => {
        closeModal(modal);
        loadScriptOnce(FEEDBACK_SCRIPT_SRC).then(() => window.checkersFeedback?.open()).catch(() => {});
    });
}

function setupGameEndModal() {
    const modal = document.getElementById('game-end-modal');
    if (!modal) return;
    setupModalBasics(modal);
    const act = key => {
        closeModal(modal);
        if (key === 'rematch') startNewGame();
        else if (key && key.startsWith('level:')) { selectLevel(key.slice(6)); startNewGame(); }
    };
    document.getElementById('game-end-primary').addEventListener('click', () => act(modal.dataset.primary));
    document.getElementById('game-end-secondary').addEventListener('click', () => act(modal.dataset.secondary));

    const shareUrl = `https://checkers.yuki-lab.com${MSG.locale === 'en' ? '/' : `/${MSG.locale}/`}`;
    const openShare = (url, method) => { window.open(url, '_blank', 'noopener,width=550,height=450'); gtagEvent('share', { method }); };
    document.getElementById('share-x').addEventListener('click', () => openShare(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareText} ${MSG.share.hashtags}`)}&url=${encodeURIComponent(shareUrl)}`, 'x'));
    document.getElementById('share-facebook').addEventListener('click', () => openShare(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, 'facebook'));
    const line = document.getElementById('share-line');
    if (MSG.locale === 'ja') {
        line.hidden = false;
        line.addEventListener('click', () => openShare(
            `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, 'line'));
    }
    const copy = document.getElementById('share-copy');
    copy.addEventListener('click', () => {
        navigator.clipboard.writeText(`${shareText}\n${shareUrl}`).then(() => {
            const label = copy.textContent;
            copy.textContent = MSG.share.copied;
            setTimeout(() => { copy.textContent = label; }, 2000);
            gtagEvent('share', { method: 'copy' });
        }).catch(err => { console.error(MSG.errors.copyFailed, err); toast(MSG.share.copyFailed); });
    });
}

// フィードバックの診断情報（feedback.js が送信時に呼ぶ）。個人情報は載せない
function setupFeedbackContext() {
    window.checkersFeedbackContext = () => {
        const context = {
            mode: IS_AI ? 'ai' : '2p',
            game: {
                moveCount: game.moveCount,
                turn: game.currentPlayer,
                gameOver: game.gameOver,
                result: game.gameResult ? game.gameResult.type : null,
                rule: game.forceCapture ? 'standard' : 'casual'
            },
            position: game.positionString(),
            moves: game.movesNotation().join(' ')
        };
        if (IS_AI) context.ai = { level: gameMeta.level, engine: gameMeta.engine || 'local', playerColor, thinking: aiThinking };
        return context;
    };
}

// ---- 初期化 -------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    loadSettings();
    if (IS_AI && AI) {
        ai = new AI.AIController({
            onMarcherStatus: status => {
                if (status === 'loading') aiStatusOverride = 'loading';
                else if (status === 'ready' && aiStatusOverride === 'loading') aiStatusOverride = null;
                else if (status === 'failed') { aiStatusOverride = 'fallback'; toast(MSG.nameTag.fallback, 4000); }
                renderNameTags();
            }
        });
        ai.setLevel(settings.level);
    }
    const restored = loadState();
    if (!restored) {
        game = new C.CheckersGame({ forceCapture: settings.forceCapture });
        playerColor = resolvePlayerColor();
        gameMeta = newGameMeta();
    } else if (IS_AI && AI) {
        // 対局途中の保存はそのレベルを引き継ぐ
        settings.level = AI.normalizeLevel(gameMeta.level);
        if (ai) ai.setLevel(settings.level);
    }

    mapSquares();
    if (board) {
        board.addEventListener('click', e => {
            hideWarn(); // 警告カードは次に盤を触ったら消す（明るいマスや縁でも）
            const el = e.target.closest('.sq');
            if (!el || el.parentNode !== board) return;
            const s = sqOfEl.get(el);
            if (s !== undefined) onSquareClick(s);
        });
        if (boardToast) boardToast.addEventListener('click', hideWarn);
    }
    renderBoard();
    renderNameTags();
    updateButtons();
    setupDifficultySelector();
    setupSettings();
    setupMenu();
    setupGameEndModal();
    setupFeedbackContext();
    setSound(settings.sound);

    document.getElementById('undo-button')?.addEventListener('click', undo);
    document.getElementById('redo-button')?.addEventListener('click', redo);
    document.getElementById('flip-button')?.addEventListener('click', flipBoard);
    document.getElementById('hint-button')?.addEventListener('click', () => { void requestHint(); });
    document.getElementById('new-game-button')?.addEventListener('click', startNewGame);
    document.getElementById('mute-button')?.addEventListener('click', () => setSound(!settings.sound));
    document.getElementById('lang-switcher')?.addEventListener('change', e => { if (e.target.value) location.href = e.target.value; });

    if (restored && game.gameOver && IS_AI) {
        // 終局後に開き直したときはモーダルを出さず、盤と名札で結果を見せるだけにする
    } else if (IS_AI && !game.gameOver && game.currentPlayer === aiColor()) {
        scheduleAIMove(600);
    }
});

window.addEventListener('pagehide', () => {
    flushSave();
    if (ai) ai.dispose();
});
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushSave();
});

window.addEventListener('load', () => {
    if (GA_ID) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
        document.head.appendChild(script);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA_ID);
    }
    const adsScript = document.createElement('script');
    adsScript.async = true;
    adsScript.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT}`;
    adsScript.crossOrigin = 'anonymous';
    document.head.appendChild(adsScript);
    whenIdle(() => {
        preloadSounds();
        loadScriptOnce(FEEDBACK_SCRIPT_SRC).catch(() => {});
    });
    // 上位レベルが選ばれているなら、暇なときにエンジンを取っておく
    if (ai && AI.usesMarcher(settings.level)) whenIdle(() => ai.preloadMarcher().catch(() => {}), 0);
});
